import React, { useState, useRef, useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import Webcam from 'react-webcam';
import { useStopwatch } from 'react-timer-hook';
import { isSafari, isMobileSafari } from 'react-device-detect';
import { Box, Typography, Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useMediaQuery, useTheme } from '@mui/material';
import PlayButton from './ekyc/PlayButton';
import stop from '../../image/stop.png';
import { notify } from '../utils';
import { getcaptchaFillInfo } from '../../api/MoadianService';

Modal.setAppElement('#root');

const VideoCaptureModal = ({ isOpen, onClose, onVideoRecorded }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    
    const webcamRef = useRef(null);
    const videoRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const [capturing, setCapturing] = useState(false);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [recordeFinish, setRecordeFinish] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [captchaText, setCaptchaText] = useState('');
    const isInitialMount = useRef(true);

    const {
        totalSeconds,
        seconds,
        minutes,
        hours,
        days,
        isRunning,
        start,
        pause,
        reset,
    } = useStopwatch({ autoStart: false });

    const modalStyle = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        content: {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            transform: 'none',
            width: isDesktop ? '70%' : '95%',
            maxHeight: '90vh',
            margin: 'auto',
            background: '#ffffff',
            overflow: 'auto',
            border: 'none',
            outline: 'none',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        }
    };
    const handleStopCaptureClick = useCallback(() => {
        try {
            pause();
            mediaRecorderRef.current.stop();
            setCapturing(false);
            showVideo();
        } catch (e) {
            setCapturing(false);
        }
    }, [mediaRecorderRef, webcamRef, setCapturing]);

    useEffect(() => {
        const getWebcamPermission = async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                setHasPermission(true);
            } catch (error) {
                console.error("Error accessing webcam: ", error);
                setHasPermission(false);
                notify("بررسی دسترسی سیستم خود را به دوربین بررسی کنید");
            }
        };

        if (isOpen) {
            getWebcamPermission();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
          setCaptchaText('لطفا خود را در ویدئویی نهایت ۱۰ ثانیه‌ای معرفی کنید');
          // Reset video state when modal opens
          setRecordedChunks([]);
          setRecordeFinish(false);
          setCapturing(false);
          if (videoRef.current) {
              videoRef.current.style.display = "none";
              videoRef.current.blob = null;
              if (videoRef.current.src) {
                  URL.revokeObjectURL(videoRef.current.src);
                  videoRef.current.src = '';
              }
          }
          reset(undefined, false);
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            if (!capturing) {
                handleDownload();
            }
        }
    }, [recordedChunks]);

    // Auto-stop recording at 10 seconds
    useEffect(() => {
        if (capturing && totalSeconds >= 10) {
            handleStopCaptureClick();
            notify("حداکثر زمان ضبط 10 ثانیه است. ضبط به صورت خودکار متوقف شد.");
        }
    }, [totalSeconds, capturing, handleStopCaptureClick]);

    // Cleanup on unmount or modal close
    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                try {
                    mediaRecorderRef.current.stop();
                } catch (e) {
                    // Ignore errors during cleanup
                }
            }
            if (videoRef.current?.src) {
                URL.revokeObjectURL(videoRef.current.src);
            }
        };
    }, []);

    const hideVideo = () => {
        if (videoRef.current) {
            videoRef.current.style.display = "none";
        }
    };

    const showVideo = () => {
        if (videoRef.current) {
            videoRef.current.style.display = "block";
        }
    };

    const handleStartCaptureClick = useCallback(() => {
        try {
            start();
            setCapturing(true);
            mediaRecorderRef.current = new MediaRecorder(webcamRef?.current?.stream, {
                mimeType: (isSafari || isMobileSafari) ? "video/mp4;codecs=avc1,mp4a" : "video/webm"
            });
            mediaRecorderRef.current.addEventListener(
                "dataavailable",
                handleDataAvailable
            );
            mediaRecorderRef.current.start();
        } catch (e) {
            setCapturing(false);
            notify("بررسی دسترسی سیستم خود را به دوربین بررسی کنید");
        }
    }, [webcamRef, setCapturing, mediaRecorderRef]);

    const handleDataAvailable = useCallback(
        ({ data }) => {
            if (data.size > 0) {
                setRecordedChunks((prev) => prev.concat(data));
            }
        },
        [setRecordedChunks]
    );

 

    const handleDownload = useCallback(() => {
        if (recordedChunks.length) {
            const blob = new Blob(recordedChunks, {
                type: (isSafari || isMobileSafari) ? "video/mp4;codecs=avc1,mp4a" : "video/webm"
            });
            const url = URL.createObjectURL(blob);
            videoRef.current.src = url;
            // Store the blob for later use
            videoRef.current.blob = blob;
        }
    }, [recordedChunks]);

    const handleConfirm = () => {
        const blob = videoRef.current?.blob;
        if (!blob || blob.size === 0) {
            notify('خطایی در ضبط ویدیو رخ داده است. لطفا مجددا تلاش نموده در صورت تکرار خطا مرورگر خود را تغییر دهید.');
            return;
        }
        
        // Check video duration - use both video element duration and stopwatch as backup
        const videoDuration = videoRef.current?.duration;
        const recordedDuration = totalSeconds;
        
        if ((videoDuration && videoDuration > 10) || (recordedDuration > 10)) {
            notify("ویدئو نباید بیشتر از 10 ثانیه باشد. لطفا ویدئوی کوتاه‌تری ضبط کنید.");
            return;
        }
        
        const url = URL.createObjectURL(blob);
        onVideoRecorded({ videoBlob: blob, videoUrl: url, captcha: captchaText });
        onClose();
    };

    const handleReset = () => {
        setRecordedChunks([]);
        hideVideo();
        reset(undefined, false);
        setRecordeFinish(false);
        if (videoRef.current) {
            videoRef.current.blob = null;
            if (videoRef.current.src) {
                URL.revokeObjectURL(videoRef.current.src);
                videoRef.current.src = '';
            }
        }
    };

    const handleClose = () => {
        if (capturing) {
            handleStopCaptureClick();
        }
        // Clean up MediaRecorder
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try {
                mediaRecorderRef.current.stop();
            } catch (e) {
                // Ignore errors during cleanup
            }
        }
        handleReset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Video Capture Modal"
            style={modalStyle}
        >
            <div className="p-4">
                <div className='row justify-content-end mt-2'>
                    <div className='col-12 col-md-10 mr-2'>
                        <div className='text-form-title text-right my-4'>
                            <span style={{ "color": "#0BA9B5" }}>۱- </span>
                            <span>درخواست مجوز دوربین توسط مرورگر خود را تایید کنید.</span>
                        </div>
                        <div className='text-form-title my-2'>
                            <span style={{ "color": "#0BA9B5" }}>۲- </span>
                            <span>بعد از زدن دکمه قرمز رنگ، کارت ملی خود را در دست گرفته و در مدت زمان مشخص شده عبارت زیر را بصورت واضح بخوانید.</span>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1.5rem' }}>
                    <div style={{ width: '100%', maxWidth: '600px' }}>
                        <p className='text-form-title text-center' style={{ color: "#0BA9B5", fontSize: "1.25rem", fontWeight: '600', margin: 0 }}>
                            {captchaText || 'در حال دریافت اطلاعات...'}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1.5rem 0', width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div 
                            id="camera" 
                            style={{ 
                                width: '300px', 
                                height: '300px', 
                                borderRadius: '50%', 
                                border: '3px solid #0BA9B5',
                                overflow: 'hidden',
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <video 
                                style={{ display: "none", borderRadius: "50%", height: "300px", width: "300px", objectFit: "cover" }} 
                                ref={videoRef} 
                                id="video-replay" 
                                height="300" 
                                width="300"
                            />
                            <Webcam 
                                className='h-[300px] w-[300px]' 
                                width={300} 
                                height={300} 
                                muted={true} 
                                mirrored={true} 
                                style={{ borderRadius: "50%", objectFit: "cover", display: videoRef.current?.style.display === "block" ? "none" : "block" }} 
                                audio={true} 
                                ref={webcamRef} 
                            />
                        </div>
                    </div>
                </div>

                {recordeFinish ? (
                    <div className='flex justify-center'>
                        <div className='text-center'>
                            <div className='flex border shadow-sm rounded p-2 justify-around'>
                                <svg 
                                    role='button' 
                                    onClick={handleReset}
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="33" 
                                    height="35" 
                                    viewBox="0 0 33 35" 
                                    fill={recordedChunks.length > 0 ? "red" : "#BCBCBC"}
                                >
                                    <path d="M16.45 0C13.4159 0 10.8896 2.29375 10.4655 5.25H4.54863C4.47498 5.23725 4.40039 5.23096 4.32567 5.2312C4.26111 5.23261 4.19675 5.23889 4.13311 5.25H1.3157C1.1438 5.24754 0.973137 5.27967 0.813619 5.34453C0.654102 5.40938 0.508915 5.50567 0.386496 5.62779C0.264077 5.74991 0.166868 5.89543 0.100518 6.05589C0.034168 6.21636 0 6.38856 0 6.5625C0 6.73644 0.034168 6.90864 0.100518 7.06911C0.166868 7.22957 0.264077 7.37509 0.386496 7.49721C0.508915 7.61933 0.654102 7.71562 0.813619 7.78047C0.973137 7.84533 1.1438 7.87746 1.3157 7.875H3.16526L5.34251 30.6506C5.57694 33.1064 7.63906 35 10.077 35H22.8213C25.2594 35 27.3215 33.1066 27.5558 30.6506L29.7347 7.875H31.5843C31.7562 7.87746 31.9269 7.84533 32.0864 7.78047C32.2459 7.71562 32.3911 7.61933 32.5135 7.49721C32.6359 7.37509 32.7331 7.22957 32.7995 7.06911C32.8658 6.90864 32.9 6.73644 32.9 6.5625C32.9 6.38856 32.8658 6.21636 32.7995 6.05589C32.7331 5.89543 32.6359 5.74991 32.5135 5.62779C32.3911 5.50567 32.2459 5.40938 32.0864 5.34453C31.9269 5.27967 31.7562 5.24754 31.5843 5.25H28.7686C28.631 5.22741 28.4907 5.22741 28.3531 5.25H22.4345C22.0104 2.29375 19.4841 0 16.45 0ZM16.45 2.625C18.0751 2.625 19.4083 3.73464 19.7893 5.25H13.1107C13.4917 3.73464 14.8249 2.625 16.45 2.625ZM5.76985 7.875H27.1285L24.9732 30.3977C24.8651 31.5309 23.9462 32.375 22.8213 32.375H10.077C8.95371 32.375 8.03317 31.5294 7.92513 30.3977L5.76985 7.875ZM13.4029 12.2312C13.0591 12.2366 12.7316 12.3799 12.4921 12.6295C12.2527 12.8791 12.121 13.2147 12.1259 13.5625V26.6875C12.1235 26.8614 12.1552 27.0341 12.2193 27.1955C12.2834 27.3569 12.3786 27.5038 12.4993 27.6276C12.62 27.7515 12.7638 27.8499 12.9224 27.917C13.081 27.9841 13.2512 28.0187 13.4231 28.0187C13.5951 28.0187 13.7653 27.9841 13.9239 27.917C14.0824 27.8499 14.2263 27.7515 14.347 27.6276C14.4677 27.5038 14.5628 27.3569 14.6269 27.1955C14.691 27.0341 14.7228 26.8614 14.7204 26.6875V13.5625C14.7229 13.3868 14.6905 13.2124 14.6251 13.0497C14.5598 12.8869 14.4628 12.7391 14.3399 12.6149C14.2171 12.4908 14.0708 12.3928 13.9099 12.3269C13.7489 12.261 13.5765 12.2285 13.4029 12.2312ZM19.4566 12.2312C19.1128 12.2366 18.7853 12.3799 18.5458 12.6295C18.3064 12.8791 18.1747 13.2147 18.1796 13.5625V26.6875C18.1772 26.8614 18.209 27.0341 18.2731 27.1955C18.3372 27.3569 18.4323 27.5038 18.553 27.6276C18.6737 27.7515 18.8176 27.8499 18.9761 27.917C19.1347 27.9841 19.3049 28.0187 19.4769 28.0187C19.6488 28.0187 19.819 27.9841 19.9776 27.917C20.1362 27.8499 20.28 27.7515 20.4007 27.6276C20.5214 27.5038 20.6166 27.3569 20.6807 27.1955C20.7448 27.0341 20.7765 26.8614 20.7741 26.6875V13.5625C20.7766 13.3868 20.7442 13.2124 20.6788 13.0497C20.6135 12.8869 20.5165 12.7391 20.3936 12.6149C20.2708 12.4908 20.1245 12.3928 19.9636 12.3269C19.8026 12.261 19.6302 12.2285 19.4566 12.2312Z" />
                                </svg>

                                {recordedChunks.length > 0 && (
                                    <PlayButton onClick={() => videoRef.current?.play()} />
                                )}

                                <svg 
                                    role='button' 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="44" 
                                    height="35" 
                                    viewBox="0 0 44 35" 
                                    fill="none"
                                >
                                    <path d="M15.9539 12.445C20.1165 8.99386 26.2032 9.44483 29.5243 13.4505C30.3743 14.4756 30.9564 15.6314 31.2917 16.8408C31.3956 17.2156 31.9131 17.324 32.1303 17.0014C32.1938 16.9009 32.2765 16.8134 32.3735 16.7439C32.4706 16.6745 32.5799 16.6246 32.6951 16.5972C32.8102 16.5698 32.9289 16.5654 33.0441 16.5843C33.1592 16.6033 33.2685 16.6451 33.3654 16.7074C33.4623 16.7697 33.5449 16.8512 33.6083 16.947C33.6717 17.0428 33.7145 17.151 33.7343 17.2652C33.7542 17.3793 33.7505 17.4972 33.7237 17.6117C33.6968 17.7262 33.6472 17.835 33.5779 17.9318L31.7023 20.7174C31.3614 21.2237 30.6503 21.3091 30.1991 20.8979L27.851 18.7573C27.7596 18.6764 27.6874 18.5764 27.6392 18.464C27.591 18.3516 27.5678 18.2293 27.5714 18.1054C27.5749 17.9815 27.605 17.8588 27.6596 17.7454C27.7142 17.632 27.7921 17.5306 27.8882 17.4479C28.0556 17.3039 28.2685 17.2248 28.4857 17.2258C28.703 17.2268 28.9091 17.3078 29.0641 17.4533C29.2933 17.6622 29.637 17.4658 29.5461 17.1693C29.2649 16.2523 28.8057 15.3769 28.1519 14.5883C25.4217 11.2954 20.4708 10.9286 17.0489 13.7657L17.023 13.7871C16.6583 14.0895 16.1176 14.039 15.8152 13.6743C15.5128 13.3096 15.5634 12.7689 15.9281 12.4665L15.9539 12.445ZM12.2501 18.6999C12.591 18.1936 13.3021 18.1082 13.7533 18.5194L16.1014 20.66C16.1884 20.7348 16.2588 20.8268 16.3083 20.9305C16.3578 21.0342 16.3856 21.1475 16.3898 21.2637C16.394 21.3799 16.3747 21.4966 16.333 21.607C16.2913 21.7174 16.228 21.8192 16.1469 21.9063C16.0658 21.9935 15.9685 22.0643 15.8608 22.1145C15.7531 22.1648 15.6371 22.1934 15.5198 22.1988C15.4025 22.2042 15.2861 22.1862 15.1777 22.1458C15.0692 22.1055 14.9708 22.0437 14.8883 21.964C14.6591 21.7551 14.3154 21.9515 14.4063 22.2479C14.6875 23.165 15.1467 24.0404 15.8004 24.829C18.5306 28.1219 23.4815 28.4887 26.9035 25.6516L26.9294 25.6301C27.2941 25.3278 27.8348 25.3783 28.1372 25.743C28.4395 26.1077 28.389 26.6484 28.0243 26.9508L27.9985 26.9722C23.8359 30.4234 17.7491 29.9725 14.4281 25.9668C13.5781 24.9417 12.996 23.7859 12.6607 22.5765C12.5568 22.2017 12.0393 22.0933 11.8221 22.4159C11.7585 22.5164 11.6758 22.6039 11.5788 22.6733C11.4818 22.7428 11.3725 22.7927 11.2573 22.8201C11.1421 22.8475 11.0235 22.8519 10.9083 22.8329C10.7932 22.814 10.6839 22.7722 10.587 22.7099C10.4901 22.6476 10.4075 22.5661 10.3441 22.4703C10.2807 22.3745 10.2378 22.2663 10.218 22.1521C10.1982 22.0379 10.2019 21.9201 10.2287 21.8056C10.2556 21.6911 10.3052 21.5822 10.3745 21.4855L12.2501 18.6999Z" fill="#0BA9B5" />
                                    <path d="M16.3598 0C15.3814 0 14.562 0.575658 14.0711 1.32401C14.0711 1.33481 14.0711 1.342 14.0711 1.3528L12.6301 3.68421H4.52083C2.03791 3.68421 0 5.76018 0 8.28947V30.3947C0 32.924 2.03791 35 4.52083 35H38.8792C41.3621 35 43.4 32.924 43.4 30.3947V8.28947C43.4 5.76018 41.3621 3.68421 38.8792 3.68421H30.6852L29.2441 1.3528C29.2441 1.342 29.2441 1.33481 29.2441 1.32401C28.7532 0.575658 27.9338 0 26.9555 0H16.3598ZM16.3598 1.84211H26.9555C27.2451 1.84211 27.4853 2.00401 27.7184 2.3602L29.4419 5.09457C29.6079 5.36081 29.894 5.52632 30.2048 5.52632H38.8792C40.3767 5.52632 41.5917 6.76398 41.5917 8.28947V30.3947C41.5917 31.9202 40.3767 33.1579 38.8792 33.1579H4.52083C3.02331 33.1579 1.80833 31.9202 1.80833 30.3947V8.28947C1.80833 6.76398 3.02331 5.52632 4.52083 5.52632H13.1104C13.4212 5.52632 13.7073 5.36081 13.8733 5.09457L15.5686 2.3602L15.5969 2.33141C15.8229 2.00761 16.0843 1.84211 16.3598 1.84211Z" fill="#0BA9B5" />
                                </svg>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='flex justify-center items-center'>
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
                            {capturing ? (
                                <Box sx={{ border: 1, borderRadius: "10px", borderColor: "#FF2B2B", boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)" }}>
                                    <Grid2 container p={1} alignItems='center'>
                                        <Grid2 xs={8} md={12}>
                                            <Typography align='right' color="#FF2B2B" variant='subtitle1' fontWeight={600} fontSize="1.2rem" textAlign={'center'}>
                                                ویدیو در حال ضبط می‌باشد
                                            </Typography>
                                            <Typography align='right' fontWeight={400} fontSize="0.9rem" textAlign={'center'}>
                                                بعد از خواندن جمله، پایان را بزنید
                                            </Typography>
                                        </Grid2>
                                        <Grid2 xs={4} md={12}>
                                            <button 
                                                onClick={handleStopCaptureClick} 
                                                className='btn text-form-button'
                                                style={{ 
                                                    backgroundColor: '#212121',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem 1.5rem',
                                                    color: 'white',
                                                    fontFamily: 'IRANYekanX',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    width: '100%'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#1a1a1a';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#212121';
                                                }}
                                            >
                                                پایان ضبط
                                            </button>
                                        </Grid2>
                                    </Grid2>
                                </Box>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    borderRadius: '12px',
                                    width: '300px',
                                    paddingTop: '0.5rem',
                                    paddingBottom: '0.5rem',
                                    paddingLeft: '1rem',
                                    paddingRight: '1rem',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <svg 
                                        role='button' 
                                        onClick={handleReset}
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="33" 
                                        height="35" 
                                        viewBox="0 0 33 35" 
                                        fill={recordedChunks.length > 0 ? "red" : "#BCBCBC"}
                                    >
                                        <path d="M16.45 0C13.4159 0 10.8896 2.29375 10.4655 5.25H4.54863C4.47498 5.23725 4.40039 5.23096 4.32567 5.2312C4.26111 5.23261 4.19675 5.23889 4.13311 5.25H1.3157C1.1438 5.24754 0.973137 5.27967 0.813619 5.34453C0.654102 5.40938 0.508915 5.50567 0.386496 5.62779C0.264077 5.74991 0.166868 5.89543 0.100518 6.05589C0.034168 6.21636 0 6.38856 0 6.5625C0 6.73644 0.034168 6.90864 0.100518 7.06911C0.166868 7.22957 0.264077 7.37509 0.386496 7.49721C0.508915 7.61933 0.654102 7.71562 0.813619 7.78047C0.973137 7.84533 1.1438 7.87746 1.3157 7.875H3.16526L5.34251 30.6506C5.57694 33.1064 7.63906 35 10.077 35H22.8213C25.2594 35 27.3215 33.1066 27.5558 30.6506L29.7347 7.875H31.5843C31.7562 7.87746 31.9269 7.84533 32.0864 7.78047C32.2459 7.71562 32.3911 7.61933 32.5135 7.49721C32.6359 7.37509 32.7331 7.22957 32.7995 7.06911C32.8658 6.90864 32.9 6.73644 32.9 6.5625C32.9 6.38856 32.8658 6.21636 32.7995 6.05589C32.7331 5.89543 32.6359 5.74991 32.5135 5.62779C32.3911 5.50567 32.2459 5.40938 32.0864 5.34453C31.9269 5.27967 31.7562 5.24754 31.5843 5.25H28.7686C28.631 5.22741 28.4907 5.22741 28.3531 5.25H22.4345C22.0104 2.29375 19.4841 0 16.45 0ZM16.45 2.625C18.0751 2.625 19.4083 3.73464 19.7893 5.25H13.1107C13.4917 3.73464 14.8249 2.625 16.45 2.625ZM5.76985 7.875H27.1285L24.9732 30.3977C24.8651 31.5309 23.9462 32.375 22.8213 32.375H10.077C8.95371 32.375 8.03317 31.5294 7.92513 30.3977L5.76985 7.875ZM13.4029 12.2312C13.0591 12.2366 12.7316 12.3799 12.4921 12.6295C12.2527 12.8791 12.121 13.2147 12.1259 13.5625V26.6875C12.1235 26.8614 12.1552 27.0341 12.2193 27.1955C12.2834 27.3569 12.3786 27.5038 12.4993 27.6276C12.62 27.7515 12.7638 27.8499 12.9224 27.917C13.081 27.9841 13.2512 28.0187 13.4231 28.0187C13.5951 28.0187 13.7653 27.9841 13.9239 27.917C14.0824 27.8499 14.2263 27.7515 14.347 27.6276C14.4677 27.5038 14.5628 27.3569 14.6269 27.1955C14.691 27.0341 14.7228 26.8614 14.7204 26.6875V13.5625C14.7229 13.3868 14.6905 13.2124 14.6251 13.0497C14.5598 12.8869 14.4628 12.7391 14.3399 12.6149C14.2171 12.4908 14.0708 12.3928 13.9099 12.3269C13.7489 12.261 13.5765 12.2285 13.4029 12.2312ZM19.4566 12.2312C19.1128 12.2366 18.7853 12.3799 18.5458 12.6295C18.3064 12.8791 18.1747 13.2147 18.1796 13.5625V26.6875C18.1772 26.8614 18.209 27.0341 18.2731 27.1955C18.3372 27.3569 18.4323 27.5038 18.553 27.6276C18.6737 27.7515 18.8176 27.8499 18.9761 27.917C19.1347 27.9841 19.3049 28.0187 19.4769 28.0187C19.6488 28.0187 19.819 27.9841 19.9776 27.917C20.1362 27.8499 20.28 27.7515 20.4007 27.6276C20.5214 27.5038 20.6166 27.3569 20.6807 27.1955C20.7448 27.0341 20.7765 26.8614 20.7741 26.6875V13.5625C20.7766 13.3868 20.7442 13.2124 20.6788 13.0497C20.6135 12.8869 20.5165 12.7391 20.3936 12.6149C20.2708 12.4908 20.1245 12.3928 19.9636 12.3269C19.8026 12.261 19.6302 12.2285 19.4566 12.2312Z" />
                                    </svg>

                                    {recordedChunks.length > 0 ? (
                                        <PlayButton onClick={() => videoRef.current?.play()} />
                                    ) : (
                                        <svg 
                                            role='button' 
                                            onClick={handleStartCaptureClick} 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="40" 
                                            height="40" 
                                            viewBox="0 0 40 40" 
                                            fill="none"
                                        >
                                            <circle cx="20" cy="20" r="20" fill="#FF2B2B" />
                                        </svg>
                                    )}

                                    <span 
                                        onClick={handleStopCaptureClick} 
                                        style={{ "cursor": "pointer", display: capturing ? "block" : "none" }}
                                    >
                                        <img src={stop} alt="stop" />
                                    </span>

                                    <svg 
                                        role='button' 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="44" 
                                        height="35" 
                                        viewBox="0 0 44 35" 
                                        fill="none"
                                    >
                                        <path d="M15.9539 12.445C20.1165 8.99386 26.2032 9.44483 29.5243 13.4505C30.3743 14.4756 30.9564 15.6314 31.2917 16.8408C31.3956 17.2156 31.9131 17.324 32.1303 17.0014C32.1938 16.9009 32.2765 16.8134 32.3735 16.7439C32.4706 16.6745 32.5799 16.6246 32.6951 16.5972C32.8102 16.5698 32.9289 16.5654 33.0441 16.5843C33.1592 16.6033 33.2685 16.6451 33.3654 16.7074C33.4623 16.7697 33.5449 16.8512 33.6083 16.947C33.6717 17.0428 33.7145 17.151 33.7343 17.2652C33.7542 17.3793 33.7505 17.4972 33.7237 17.6117C33.6968 17.7262 33.6472 17.835 33.5779 17.9318L31.7023 20.7174C31.3614 21.2237 30.6503 21.3091 30.1991 20.8979L27.851 18.7573C27.7596 18.6764 27.6874 18.5764 27.6392 18.464C27.591 18.3516 27.5678 18.2293 27.5714 18.1054C27.5749 17.9815 27.605 17.8588 27.6596 17.7454C27.7142 17.632 27.7921 17.5306 27.8882 17.4479C28.0556 17.3039 28.2685 17.2248 28.4857 17.2258C28.703 17.2268 28.9091 17.3078 29.0641 17.4533C29.2933 17.6622 29.637 17.4658 29.5461 17.1693C29.2649 16.2523 28.8057 15.3769 28.1519 14.5883C25.4217 11.2954 20.4708 10.9286 17.0489 13.7657L17.023 13.7871C16.6583 14.0895 16.1176 14.039 15.8152 13.6743C15.5128 13.3096 15.5634 12.7689 15.9281 12.4665L15.9539 12.445ZM12.2501 18.6999C12.591 18.1936 13.3021 18.1082 13.7533 18.5194L16.1014 20.66C16.1884 20.7348 16.2588 20.8268 16.3083 20.9305C16.3578 21.0342 16.3856 21.1475 16.3898 21.2637C16.394 21.3799 16.3747 21.4966 16.333 21.607C16.2913 21.7174 16.228 21.8192 16.1469 21.9063C16.0658 21.9935 15.9685 22.0643 15.8608 22.1145C15.7531 22.1648 15.6371 22.1934 15.5198 22.1988C15.4025 22.2042 15.2861 22.1862 15.1777 22.1458C15.0692 22.1055 14.9708 22.0437 14.8883 21.964C14.6591 21.7551 14.3154 21.9515 14.4063 22.2479C14.6875 23.165 15.1467 24.0404 15.8004 24.829C18.5306 28.1219 23.4815 28.4887 26.9035 25.6516L26.9294 25.6301C27.2941 25.3278 27.8348 25.3783 28.1372 25.743C28.4395 26.1077 28.389 26.6484 28.0243 26.9508L27.9985 26.9722C23.8359 30.4234 17.7491 29.9725 14.4281 25.9668C13.5781 24.9417 12.996 23.7859 12.6607 22.5765C12.5568 22.2017 12.0393 22.0933 11.8221 22.4159C11.7585 22.5164 11.6758 22.6039 11.5788 22.6733C11.4818 22.7428 11.3725 22.7927 11.2573 22.8201C11.1421 22.8475 11.0235 22.8519 10.9083 22.8329C10.7932 22.814 10.6839 22.7722 10.587 22.7099C10.4901 22.6476 10.4075 22.5661 10.3441 22.4703C10.2807 22.3745 10.2378 22.2663 10.218 22.1521C10.1982 22.0379 10.2019 21.9201 10.2287 21.8056C10.2556 21.6911 10.3052 21.5822 10.3745 21.4855L12.2501 18.6999Z" fill="#0BA9B5" />
                                        <path d="M16.3598 0C15.3814 0 14.562 0.575658 14.0711 1.32401C14.0711 1.33481 14.0711 1.342 14.0711 1.3528L12.6301 3.68421H4.52083C2.03791 3.68421 0 5.76018 0 8.28947V30.3947C0 32.924 2.03791 35 4.52083 35H38.8792C41.3621 35 43.4 32.924 43.4 30.3947V8.28947C43.4 5.76018 41.3621 3.68421 38.8792 3.68421H30.6852L29.2441 1.3528C29.2441 1.342 29.2441 1.33481 29.2441 1.32401C28.7532 0.575658 27.9338 0 26.9555 0H16.3598ZM16.3598 1.84211H26.9555C27.2451 1.84211 27.4853 2.00401 27.7184 2.3602L29.4419 5.09457C29.6079 5.36081 29.894 5.52632 30.2048 5.52632H38.8792C40.3767 5.52632 41.5917 6.76398 41.5917 8.28947V30.3947C41.5917 31.9202 40.3767 33.1579 38.8792 33.1579H4.52083C3.02331 33.1579 1.80833 31.9202 1.80833 30.3947V8.28947C1.80833 6.76398 3.02331 5.52632 4.52083 5.52632H13.1104C13.4212 5.52632 13.7073 5.36081 13.8733 5.09457L15.5686 2.3602L15.5969 2.33141C15.8229 2.00761 16.0843 1.84211 16.3598 1.84211Z" fill="#0BA9B5" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    marginTop: '1.5rem',
                    gap: '0.75rem'
                }}>
                    <button
                        className='btn text-form-title'
                        onClick={handleClose}
                        style={{ 
                            minWidth: '120px',
                            padding: '0.75rem 1.5rem',
                            border: '2px solid #D3D3D3',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            color: '#131313',
                            fontFamily: 'IRANYekanX',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#A0A0A0';
                            e.target.style.backgroundColor = '#F5F5F5';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#D3D3D3';
                            e.target.style.backgroundColor = 'white';
                        }}
                    >
                        بستن
                    </button>
                    <button
                        className='btn text-form-button'
                        onClick={handleConfirm}
                        style={{ 
                            minWidth: '120px',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            backgroundColor: '#044448',
                            color: 'white',
                            fontFamily: 'IRANYekanX',
                            fontSize: '16px',
                            fontWeight: '600',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#022224';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#044448';
                        }}
                    >
                        تایید
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default VideoCaptureModal;

